<div class="remit-import" [class.remit-import--expanded]="expanded" *ngIf="vendor && vendor.supplierid">
	<div class="remit-header" (click)="toggle()" [class.is-open]="open">
		<i class="material-icons remit-header__icon">receipt_long</i>
		<div class="remit-header__text">
			<span class="remit-header__title">Import Remittance / Draft Detail</span>
			<span class="remit-header__sub">Match the vendor's remit to open invoices and fill the allocations in one click</span>
		</div>
		<span class="remit-header__step" *ngIf="open && mode === 'review' && batch">{{ includedCount }} of {{ batch.recon.line_count }} selected</span>
		<i class="material-icons remit-header__rematch" *ngIf="open && mode === 'review' && batch"
			[class.is-spinning]="parsing"
			(click)="$event.stopPropagation(); retry()"
			matTooltip="Re-match — re-read &amp; re-match without re-uploading">replay</i>
		<i class="material-icons remit-header__clear" *ngIf="open && (batch || pasteText || fileName)"
			(click)="$event.stopPropagation(); reset()"
			matTooltip="Clear — discard and start over">refresh</i>
		<i class="material-icons remit-header__expand" *ngIf="open"
			(click)="$event.stopPropagation(); toggleExpand()"
			[matTooltip]="expanded ? 'Exit focus' : 'Focus mode'">{{ expanded ? 'close_fullscreen' : 'open_in_full' }}</i>
		<i class="material-icons remit-header__chev">{{ open ? 'expand_less' : 'expand_more' }}</i>
	</div>

	<div class="remit-body" *ngIf="open">

		<!-- STEP 1: input -->
		<ng-container *ngIf="mode === 'input'">
			<div class="remit-progress" *ngIf="parsing">
				<div class="remit-progress__spin"></div>
				<div class="remit-progress__text">
					<div class="remit-progress__title">{{ parsingLabel }}</div>
					<div class="remit-progress__sub">{{ parsingSub }}</div>
				</div>
				<div class="remit-progress__bar"><span></span></div>
			</div>

			<div class="remit-input" [class.is-hidden]="parsing">
				<div class="remit-input__pane">
					<div class="remit-input__label">Paste remittance text</div>
					<mat-form-field appearance="outline" class="full-width">
						<textarea matInput rows="9" [(ngModel)]="pasteText" [ngModelOptions]="{standalone: true}"
							placeholder="Paste the remit rows — invoice # / date / amount…"></textarea>
					</mat-form-field>
				</div>

				<div class="remit-input__or"><span>or</span></div>

				<div class="remit-input__pane">
					<div class="remit-input__label">Upload a file</div>
					<label class="dropzone" [class.dropzone--active]="dragging" [class.dropzone--filled]="fileName"
						(dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
						<i class="material-icons">{{ fileName ? 'description' : (dragging ? 'file_download' : 'cloud_upload') }}</i>
						<span *ngIf="!fileName" class="dropzone__hint">Drop a <b>PDF</b>, <b>Excel</b>, or <b>CSV</b> here<br><span class="dropzone__sub">or click to browse</span></span>
						<span *ngIf="fileName" class="dropzone__file">{{ fileName }}</span>
						<button *ngIf="fileName" type="button" mat-icon-button class="dropzone__remove"
							(click)="$event.preventDefault(); $event.stopPropagation(); clearFile()"><i class="material-icons">close</i></button>
						<input type="file" accept=".pdf,.csv,.tsv,.txt,.xlsx,.xls" (change)="onFile($event); $event.target.value=''" hidden>
					</label>
				</div>
			</div>

			<div class="remit-foot" [class.is-hidden]="parsing">
				<span class="remit-foot__note">Nothing posts — this only pre-fills the allocation checkboxes below.</span>
				<button type="button" mat-raised-button color="primary" [disabled]="!canParse()" (click)="parse()">
					<i class="material-icons">auto_awesome</i>
					Parse &amp; Match
				</button>
			</div>
		</ng-container>

		<!-- STEP 2: review -->
		<ng-container *ngIf="mode === 'review' && batch">
			<div class="remit-applied" *ngIf="appliedToPayment">
				<i class="material-icons">playlist_add_check</i>
				<span class="remit-applied__text">
					Applied to the payment — post it right here, or review the payment form first. Re-apply any time after edits.
					<b *ngIf="creditPlan">{{ creditPlan.credit_applied_total | currency }} of credit is queued against these invoices; the payment covers the {{ creditPlan.cash_required_total | currency }} balance.</b>
				</span>
				<button type="button" mat-raised-button color="primary" class="remit-applied__post" (click)="requestPost()">
					<i class="material-icons">account_balance</i> Post Payment
				</button>
			</div>

			<div class="remit-summary">
				<div class="remit-summary__totals">
					<div class="stat">
						<span class="stat__label">Remit total</span>
						<span class="stat__value">{{ statedTotal | currency }}</span>
					</div>
					<i class="material-icons stat__arrow">chevron_right</i>
					<div class="stat" *ngIf="bankAmount !== null">
						<span class="stat__label">Bank line</span>
						<span class="stat__value">{{ bankAmount | currency }}</span>
					</div>
					<i class="material-icons stat__arrow" *ngIf="bankAmount !== null">chevron_right</i>
					<div class="stat">
						<span class="stat__label">Applying</span>
						<span class="stat__value stat__value--accent">{{ appliedTotal | currency }}</span>
						<span class="stat__sub">{{ includedCount }} of {{ batch.recon.line_count }} lines</span>
					</div>
					<div class="stat stat--delta" *ngIf="delta !== 0">
						<span class="stat__label">Difference</span>
						<span class="stat__value" [class.stat__value--warn]="!deltaIsCredits" [class.stat__value--accent]="deltaIsCredits">{{ delta | currency }}</span>
						<span class="stat__sub" *ngIf="deltaIsCredits">
							= the {{ creditLines.length }} credits ({{ openCreditTotal | currency }}) — draft is net{{ applyCredits ? ', they will be applied' : '' }}
						</span>
						<span class="stat__sub" *ngIf="!deltaIsCredits && openCreditTotal">of which credits: {{ openCreditTotal | currency }}</span>
					</div>
					<div class="remit-summary__ok" *ngIf="delta === 0 && includedCount">
						<i class="material-icons">check_circle</i> Balances{{ bankAmount !== null ? ' to the bank line' : ' to the remittance' }}
					</div>
				</div>
				<div class="remit-summary__right">
					<div class="remit-summary__exports">
						<button type="button" mat-stroked-button (click)="exportXlsx()"><i class="material-icons">table_view</i> Excel</button>
						<button type="button" mat-stroked-button (click)="exportPdf()"><i class="material-icons">picture_as_pdf</i> PDF</button>
					</div>
					<div class="remit-summary__chips">
						<span class="chip chip--btn st-action" [class.chip--active]="filterStatus==='action'" *ngIf="actionCount" (click)="setFilter('action')">
							<b>{{ actionCount }}</b> need action<span class="chip__amt">{{ actionTotal | currency }}</span></span>
						<span class="chip chip--btn st-all" [class.chip--active]="!filterStatus" (click)="filterStatus=null">
							<b>all</b> {{ batch.recon.line_count }}<span class="chip__amt">{{ statusTotal(null) | currency }}</span></span>
						<span class="chip chip--btn st-matched" [class.chip--active]="filterStatus==='matched'" *ngIf="batch.recon.counts.matched" (click)="setFilter('matched')">
							<b>{{ batch.recon.counts.matched }}</b> matched<span class="chip__amt">{{ statusTotal('matched') | currency }}</span></span>
						<span class="chip chip--btn st-partial" [class.chip--active]="filterStatus==='partial'" *ngIf="batch.recon.counts.partial" (click)="setFilter('partial')">
							<b>{{ batch.recon.counts.partial }}</b> partial<span class="chip__amt">{{ statusTotal('partial') | currency }}</span></span>
						<span class="chip chip--btn st-over" [class.chip--active]="filterStatus==='over'" *ngIf="batch.recon.counts.over" (click)="setFilter('over')">
							<b>{{ batch.recon.counts.over }}</b> over<span class="chip__amt">{{ statusTotal('over') | currency }}</span></span>
						<span class="chip chip--btn st-multiple" [class.chip--active]="filterStatus==='multiple'" *ngIf="batch.recon.counts.multiple" (click)="setFilter('multiple')">
							<b>{{ batch.recon.counts.multiple }}</b> multiple<span class="chip__amt">{{ statusTotal('multiple') | currency }}</span></span>
						<span class="chip chip--btn st-settled" [class.chip--active]="filterStatus==='already_settled'" *ngIf="batch.recon.counts.already_settled" (click)="setFilter('already_settled')">
							<b>{{ batch.recon.counts.already_settled }}</b> settled<span class="chip__amt">{{ statusTotal('already_settled') | currency }}</span></span>
						<span class="chip chip--btn st-credit" [class.chip--active]="filterStatus==='credit'" *ngIf="batch.recon.counts.credit" (click)="setFilter('credit')">
							<b>{{ batch.recon.counts.credit }}</b> credit<span class="chip__amt">{{ statusTotal('credit') | currency }}</span></span>
						<span class="chip chip--btn st-unmatched" [class.chip--active]="filterStatus==='unmatched'" *ngIf="batch.recon.counts.unmatched" (click)="setFilter('unmatched')">
							<b>{{ batch.recon.counts.unmatched }}</b> unmatched<span class="chip__amt">{{ statusTotal('unmatched') | currency }}</span></span>
					</div>
				</div>
			</div>

			<div class="remit-tabletools">
				<div class="remit-search">
					<i class="material-icons">search</i>
					<input type="text" [(ngModel)]="searchText" [ngModelOptions]="{standalone: true}" placeholder="Find invoice #, amount, or note…">
					<i class="material-icons remit-search__clear" *ngIf="searchText" (click)="searchText=''">close</i>
				</div>
				<span class="remit-tabletools__count">{{ filteredLines.length }} of {{ batch.recon.line_count }}</span>
			</div>

			<div class="table-wrap">
				<table class="remit-table">
					<thead>
						<tr>
							<th class="col-post">
								<mat-checkbox [checked]="allSelected" (change)="toggleAll()" matTooltip="Select all matched"></mat-checkbox>
							</th>
							<th class="th-sort" (click)="setSort('invoice_ref')">Invoice Ref<i class="material-icons th-arrow" *ngIf="sortKey==='invoice_ref'">{{ sortDir===1 ? 'arrow_upward' : 'arrow_downward' }}</i></th>
							<th>Description</th>
							<th class="num th-sort" (click)="setSort('paid_amount')">Paid<i class="material-icons th-arrow" *ngIf="sortKey==='paid_amount'">{{ sortDir===1 ? 'arrow_upward' : 'arrow_downward' }}</i></th>
							<th class="th-sort" (click)="setSort('matched_transno')">ERP Doc<i class="material-icons th-arrow" *ngIf="sortKey==='matched_transno'">{{ sortDir===1 ? 'arrow_upward' : 'arrow_downward' }}</i></th>
							<th class="num th-sort" (click)="setSort('matched_balance')">Balance<i class="material-icons th-arrow" *ngIf="sortKey==='matched_balance'">{{ sortDir===1 ? 'arrow_upward' : 'arrow_downward' }}</i></th>
							<th class="th-sort" (click)="setSort('match_status')">Status<i class="material-icons th-arrow" *ngIf="sortKey==='match_status'">{{ sortDir===1 ? 'arrow_upward' : 'arrow_downward' }}</i></th>
							<th class="num th-sort" (click)="setSort('alloc_amount')">Allocate<i class="material-icons th-arrow" *ngIf="sortKey==='alloc_amount'">{{ sortDir===1 ? 'arrow_upward' : 'arrow_downward' }}</i></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let line of filteredLines" [class.excluded]="!line.include" [class.unmatchable]="!line.matched_supptransid">
							<td class="col-post"><mat-checkbox [checked]="line.include" (change)="toggleInclude(line)" [disabled]="!isSelectable(line)"></mat-checkbox></td>
							<td class="mono">{{ line.invoice_ref }}</td>
							<td class="desc" [title]="line.description">{{ line.description || '—' }}</td>
							<td class="num">{{ line.paid_amount | currency }}</td>
							<td class="mono">{{ line.matched_transno || '—' }}</td>
							<td class="num">{{ line.matched_balance !== null ? (line.matched_balance | currency) : '—' }}</td>
							<td>
								<span class="chip" [ngClass]="statusClass(line.match_status)" [class.chip--btn]="hasDetail(line.match_status)"
									[matTooltip]="hasDetail(line.match_status) ? 'Click for details' : line.match_note"
									(click)="openDetail(line)">
									{{ statusLabel(line.match_status) }}<i *ngIf="hasDetail(line.match_status)" class="material-icons chip__i">info</i>
								</span>
								<span class="row-gap" *ngIf="lineGap(line)">{{ lineGap(line) }}</span>
							</td>
							<td class="num">
								<input type="number" step="0.01" class="alloc-input" [value]="line.alloc_amount"
									(input)="updateAlloc(line, $event.target.value)" [disabled]="!line.include">
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div class="remit-foot">
				<div class="remit-foot__left">
					<mat-checkbox *ngIf="creditLines.length" class="remit-foot__credits" [(ngModel)]="applyCredits"
						[ngModelOptions]="{standalone:true}"
						matTooltip="Pairs each credit note to the invoices on this draft, oldest first, capped at what each still has open. The payment then covers only the cash difference.">
						Apply the {{ creditLines.length }} credit notes ({{ creditAvailableTotal | currency }})
					</mat-checkbox>
					<span class="remit-foot__note" *ngIf="filterStatus">Filtered view — applying still uses all included lines.</span>
				</div>
				<button type="button" mat-button (click)="reset()">Cancel</button>
				<div class="remit-foot__total">
					<span class="remit-foot__total-label">Will apply</span>
					<span class="remit-foot__total-value">{{ appliedTotal | currency }}</span>
					<span class="remit-foot__total-sub" *ngIf="applyCredits && creditLines.length">
						{{ (appliedTotal - creditAvailableTotal) > 0 ? ((appliedTotal - creditAvailableTotal) | currency) : (0 | currency) }} cash + credits
					</span>
				</div>
				<button type="button" mat-raised-button color="primary" [disabled]="appliedTotal <= 0 || applying" (click)="applyToPayment()">
					<i class="material-icons">playlist_add_check</i>
					<span *ngIf="applying">Verifying…</span>
					<span *ngIf="!applying">{{ appliedToPayment ? 'Re-apply to Payment' : 'Apply to Payment' }}</span>
				</button>
			</div>
		</ng-container>

		<!-- STEP 3: done (host reported the payment posted) -->
		<ng-container *ngIf="mode === 'done'">
			<div class="done-box">
				<i class="material-icons">check_circle</i>
				<div class="done-box__text">
					<h4>Payment posted</h4>
					<p>{{ appliedResult?.payment_ref ? 'Reference ' + appliedResult.payment_ref + ' · ' : '' }}the remittance batch is archived against it.</p>
				</div>
				<button type="button" mat-raised-button color="primary" (click)="reset()">Import another</button>
			</div>
		</ng-container>

	</div>

	<!-- line detail modal -->
	<div class="remit-modal" *ngIf="detailLine" (click)="closeDetail()">
		<div class="remit-modal__card" (click)="$event.stopPropagation()">
			<div class="remit-modal__head">
				<span class="remit-modal__title">Invoice Ref <b>{{ detailLine.invoice_ref }}</b></span>
				<span class="chip" [ngClass]="statusClass(detailLine.match_status)">{{ statusLabel(detailLine.match_status) }}</span>
				<button type="button" class="remit-modal__x" (click)="closeDetail()"><i class="material-icons">close</i></button>
			</div>
			<div class="remit-modal__body">
				<div *ngIf="detailLoading" class="remit-modal__muted">Loading…</div>
				<div *ngIf="!detailLoading && detailData?.error" class="remit-modal__muted">{{ detailData.error }}</div>

				<ng-container *ngIf="!detailLoading && detailData && !detailData.error">
					<div class="remit-modal__amt">
						<span class="remit-modal__amt-l">Remittance amount</span>
						<b>{{ detailLine.paid_amount | currency }}</b>
					</div>

					<p *ngIf="detailLine.match_status === 'credit' && detailData.documents?.length" class="remit-modal__hint">
						Credit the vendor netted from the draft — apply it in the Allocate section, not on the payment. Backed by:
					</p>

					<div *ngIf="!detailData.documents?.length" class="remit-modal__muted">
						No document with reference {{ detailLine.invoice_ref }} on this vendor.
					</div>
					<div *ngFor="let d of detailData.documents" class="doc">
						<div class="doc__hd">
							<span class="doc__title">{{ d.doc }} #{{ d.transno }}</span>
							<span class="doc__meta">{{ d.date }} · {{ d.face | currency }}<span *ngIf="d.suppreference"> · {{ d.suppreference }}</span></span>
							<span class="chip" [ngClass]="d.settled ? 'st-settled' : 'st-matched'">{{ d.settled ? 'settled' : 'open · ' + (d.balance | currency) }}</span>
						</div>
						<div class="doc__rows" *ngIf="d.settled_by?.length">
							<div class="drow" *ngFor="let p of d.settled_by">
								<span class="drow__date">{{ p.date }}</span>
								<span class="drow__by"><b>{{ p.doc }}</b><ng-container *ngIf="p.by"> · {{ p.by }}</ng-container></span>
								<span class="drow__ref">#{{ p.transno }}</span>
								<span class="drow__amt">{{ p.amount | currency }}</span>
							</div>
						</div>
						<div *ngIf="!d.settled_by?.length" class="remit-modal__muted doc__empty">Nothing applied to it yet.</div>
					</div>
				</ng-container>
			</div>
		</div>
	</div>
</div>
